import {
  PartnerRevenueRecord,
  PaymentOption,
  RefundReasonCode,
  RefundReasonCodeLabels,
  RevenueCustomerType,
  RevenueTotalsRecord,
  RevenueWindowRecord
} from '@navio/nsdk';
import { eachDayOfInterval, isSunday, min, parseISO } from 'date-fns';
import { getLabelMapFromEntries } from 'noddi-util';

import { buildChart } from './revenueChart';
import { OTHER_SERVICES_COLOR, rankedServiceColors, type RevenueColor, serviceIcon } from './revenueStyles';
import {
  change,
  CUSTOMER_TYPE_LABELS,
  earned,
  PAYMENT_OPTION_LABELS,
  periodIndex,
  pointsChange,
  rankServices,
  ratio,
  refundRate,
  revenue,
  type RevenueFilters,
  type RevenueSplit,
  serviceCells,
  serviceName,
  sum,
  windowCells
} from './revenueTotals';

const REFUND_REASON_LABELS = getLabelMapFromEntries(RefundReasonCode, RefundReasonCodeLabels);

/** Every figure on the revenue page, derived from one response so filters and splits never refetch. */
export const buildRevenueView = ({
  data,
  filters,
  includeUpcoming,
  split,
  today
}: {
  data: PartnerRevenueRecord;
  filters: RevenueFilters;
  includeUpcoming: boolean;
  split: RevenueSplit;
  today: string;
}) => {
  const todayIndex = lastIndexOnOrBefore(data.current.periodStarts, today);
  const lastIndex = includeUpcoming ? data.current.periodStarts.length - 1 : todayIndex;
  const serviceNames = new Map(data.serviceCategories.map((category) => [category.id, category.name]));
  // Ranked without the service filter, so a service keeps its color when it is the one selected.
  const serviceColors = rankedServiceColors(
    rankServices(serviceCells(data.current, { ...filters, serviceCategoryId: null }))
  );

  // Shown periods for totals; "so far" periods (up to today) for comparisons, so booked work never counts
  // against a past that is complete.
  const shown = (revenueWindow: RevenueWindowRecord, cells: RevenueTotalsRecord[]) =>
    cells.filter((cell) => periodIndex(revenueWindow, cell) <= lastIndex);
  const soFar = (revenueWindow: RevenueWindowRecord, cells: RevenueTotalsRecord[]) =>
    cells.filter((cell) => periodIndex(revenueWindow, cell) <= todayIndex);

  const current = sum(shown(data.current, windowCells(data.current, filters)));
  const currentSoFar = sum(soFar(data.current, windowCells(data.current, filters)));
  const previousSoFar = sum(soFar(data.comparison, windowCells(data.comparison, filters)));

  const workingDays = countWorkingDays(data.current.fromDate, min([parseISO(today), parseISO(data.current.toDate)]));
  const comparisonWorkingDays = countWorkingDays(
    data.comparison.fromDate,
    parseISO(data.comparison.periodStarts[todayIndex] ?? data.comparison.toDate)
  );

  const tableCells = (revenueWindow: RevenueWindowRecord, ignore: keyof RevenueFilters) =>
    shown(revenueWindow, windowCells(revenueWindow, { ...filters, [ignore]: null }));
  const tableCellsSoFar = (revenueWindow: RevenueWindowRecord, ignore: keyof RevenueFilters) =>
    soFar(revenueWindow, windowCells(revenueWindow, { ...filters, [ignore]: null }));

  return {
    todayIndex,
    hero: {
      expected: revenue(current),
      collected: current.collected,
      awaitingPayment: current.awaitingPayment,
      overdue: current.overdue,
      projected: current.projected,
      refunded: current.refunded,
      change: change(revenue(currentSoFar), revenue(previousSoFar))
    },
    kpis: {
      cars: currentSoFar.carsCompleted,
      bookings: currentSoFar.bookingsCompleted,
      carsChange: change(currentSoFar.carsCompleted, previousSoFar.carsCompleted),
      upcomingCars: current.carsUpcoming,
      perCar: ratio(earned(currentSoFar), currentSoFar.carsCompleted),
      perCarChange: change(
        ratio(earned(currentSoFar), currentSoFar.carsCompleted),
        ratio(earned(previousSoFar), previousSoFar.carsCompleted)
      ),
      workingDays,
      perWorkingDay: ratio(earned(currentSoFar), workingDays),
      perWorkingDayChange: change(
        ratio(earned(currentSoFar), workingDays),
        ratio(earned(previousSoFar), comparisonWorkingDays)
      )
    },
    chart: buildChart({ data, filters, split, lastIndex, serviceNames, serviceColors }),
    methodRows: withShares(
      Object.values(PaymentOption).map((paymentOption) => {
        const totals = sum(
          tableCells(data.current, 'paymentOption').filter((cell) => cell.paymentOption === paymentOption)
        );
        return { paymentOption, ...PAYMENT_OPTION_LABELS[paymentOption], ...totals, total: revenue(totals) };
      }),
      (row) => row.total
    ),
    serviceRows: buildServiceRows({ data, tableCells, tableCellsSoFar, serviceNames, serviceColors }),
    customerTypeRows: withShares(
      Object.values(RevenueCustomerType).map((customerType) => {
        const totals = sum(
          tableCells(data.current, 'customerType').filter((cell) => cell.customerType === customerType)
        );
        const nowSoFar = sum(
          tableCellsSoFar(data.current, 'customerType').filter((cell) => cell.customerType === customerType)
        );
        const before = sum(
          tableCellsSoFar(data.comparison, 'customerType').filter((cell) => cell.customerType === customerType)
        );
        return {
          customerType,
          name: CUSTOMER_TYPE_LABELS[customerType],
          earned: earned(totals),
          projected: totals.projected,
          cars: totals.carsCompleted,
          perCar: ratio(earned(totals), totals.carsCompleted),
          change: change(revenue(nowSoFar), revenue(before))
        };
      }),
      (row) => row.earned + row.projected
    ),
    refunds: {
      refunded: current.refunded,
      rate: refundRate(current),
      rateChangePoints: pointsChange(refundRate(currentSoFar), refundRate(previousSoFar)),
      refundedChange: change(currentSoFar.refunded, previousSoFar.refunded),
      bookings: current.bookingsRefunded,
      bookingsChange: change(currentSoFar.bookingsRefunded, previousSoFar.bookingsRefunded),
      reasons: buildReasons({ data, filters })
    },
    accounts: buildAccounts({ data, filters })
  };
};

export type RevenueView = ReturnType<typeof buildRevenueView>;

const buildServiceRows = ({
  data,
  tableCells,
  tableCellsSoFar,
  serviceNames,
  serviceColors
}: {
  data: PartnerRevenueRecord;
  tableCells: (revenueWindow: RevenueWindowRecord, ignore: keyof RevenueFilters) => RevenueTotalsRecord[];
  tableCellsSoFar: (revenueWindow: RevenueWindowRecord, ignore: keyof RevenueFilters) => RevenueTotalsRecord[];
  serviceNames: Map<number, string>;
  serviceColors: Map<number | null, RevenueColor>;
}) => {
  const serviceTypes = new Map(data.serviceCategories.map((category) => [category.id, category.type]));
  // The service table never narrows itself: it lists every service under the other filters.
  const forService = (revenueWindow: RevenueWindowRecord, cells: RevenueTotalsRecord[], id: number | null) => {
    const allowed = new Set(cells.map(cellKey));
    return revenueWindow.byService.filter((cell) => cell.serviceCategoryId === id && allowed.has(cellKey(cell)));
  };
  const currentCells = tableCells(data.current, 'serviceCategoryId');
  const currentSoFar = tableCellsSoFar(data.current, 'serviceCategoryId');
  const previousSoFar = tableCellsSoFar(data.comparison, 'serviceCategoryId');
  const ids = rankServices(forAnyService(data.current, currentCells));
  const total = ids
    .map((id) => earned(sum(forService(data.current, currentCells, id))))
    .reduce((acc, value) => acc + value, 0);
  return ids.map((id) => {
    const totals = sum(forService(data.current, currentCells, id));
    const gross = revenue(totals) + totals.refunded;
    return {
      serviceCategoryId: id,
      name: serviceName(id, serviceNames),
      icon: serviceIcon(id === null ? null : serviceTypes.get(id)),
      color: serviceColors.get(id) ?? OTHER_SERVICES_COLOR,
      earned: earned(totals),
      projected: totals.projected,
      refunded: totals.refunded,
      refundRate: gross > 0 ? (totals.refunded / gross) * 100 : null,
      share: total > 0 ? (earned(totals) / total) * 100 : 0,
      change: change(
        revenue(sum(forService(data.current, currentSoFar, id))),
        revenue(sum(forService(data.comparison, previousSoFar, id)))
      )
    };
  });
};

/** Service rows of the same periods, payment options and customer types as the given total cells. */
const forAnyService = (revenueWindow: RevenueWindowRecord, cells: RevenueTotalsRecord[]) => {
  const allowed = new Set(cells.map(cellKey));
  return revenueWindow.byService.filter((cell) => allowed.has(cellKey(cell)));
};

const cellKey = (cell: RevenueTotalsRecord) => `${cell.periodStart}|${cell.paymentOption}|${cell.customerType}`;

const buildReasons = ({ data, filters }: { data: PartnerRevenueRecord; filters: RevenueFilters }) => {
  const rows = (
    filters.serviceCategoryId === null
      ? data.refundReasons
      : data.refundReasonsByService.filter((row) => row.serviceCategoryId === filters.serviceCategoryId)
  ).filter(
    (row) =>
      (filters.paymentOption === null || row.paymentOption === filters.paymentOption) &&
      (filters.customerType === null || row.customerType === filters.customerType)
  );
  const byReason = new Map<RefundReasonCode, { refunded: number; bookings: number }>();
  for (const row of rows) {
    const entry = byReason.get(row.reasonCode) ?? { refunded: 0, bookings: 0 };
    entry.refunded += row.refunded;
    entry.bookings += row.bookings;
    byReason.set(row.reasonCode, entry);
  }
  const total = [...byReason.values()].reduce((acc, entry) => acc + entry.refunded, 0);
  return [...byReason.entries()]
    .map(([reasonCode, entry]) => ({
      reasonCode,
      label: REFUND_REASON_LABELS[reasonCode],
      ...entry,
      share: total > 0 ? (entry.refunded / total) * 100 : 0
    }))
    .sort((a, b) => b.refunded - a.refunded);
};

const TOP_ACCOUNTS = 5;

const buildAccounts = ({ data, filters }: { data: PartnerRevenueRecord; filters: RevenueFilters }) => {
  const rows = (
    filters.serviceCategoryId === null
      ? data.businessAccounts
      : data.businessAccountsByService.filter((row) => row.serviceCategoryId === filters.serviceCategoryId)
  ).filter((row) => filters.paymentOption === null || row.paymentOption === filters.paymentOption);
  const byAccount = new Map<
    number,
    { userGroupId: number; name: string; revenue: number; comparisonRevenue: number; cars: number }
  >();
  for (const row of rows) {
    const entry = byAccount.get(row.userGroupId) ?? {
      userGroupId: row.userGroupId,
      name: row.name,
      revenue: 0,
      comparisonRevenue: 0,
      cars: 0
    };
    entry.revenue += row.revenue;
    entry.comparisonRevenue += row.comparisonRevenue;
    entry.cars += row.carsCompleted;
    byAccount.set(row.userGroupId, entry);
  }
  return [...byAccount.values()]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, TOP_ACCOUNTS)
    .map((entry) => ({ ...entry, change: change(entry.revenue, entry.comparisonRevenue) }));
};

/** Each row's part of the rows' total, in percent, for the share bars. */
const withShares = <Row>(rows: Row[], amount: (row: Row) => number) => {
  const total = rows.reduce((acc, row) => acc + amount(row), 0);
  return rows.map((row) => ({ ...row, share: total > 0 ? (amount(row) / total) * 100 : 0 }));
};

const lastIndexOnOrBefore = (periodStarts: string[], day: string) =>
  periodStarts.reduce((last, periodStart, index) => (periodStart <= day ? index : last), -1);

const countWorkingDays = (from: string, until: Date) => {
  const start = parseISO(from);
  if (until < start) {
    return 0;
  }
  // Workshops open Monday to Saturday.
  return eachDayOfInterval({ start, end: until }).filter((day) => !isSunday(day)).length;
};
